/**
 * useRooms — the React binding for the rooms feature.
 *
 * Strict layering: pages call THIS hook; the hook calls {@link RoomStore}; the
 * store calls the api facade. Pages never import the store or the api directly.
 *
 * Exposes the room summaries list (with loading/error), a refresh, and thin
 * async mutation/read operations that delegate to the store.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { RoomStore, type RoomDraft } from './roomStore';
import { sortRoomsByRecency } from './roomModel';
import type { Room, RoomMember, RoomSummary } from './types';

export interface UseRoomsResult {
  /** Non-deleted room summaries, newest-first. */
  summaries: RoomSummary[];
  loading: boolean;
  error: string | null;
  /** Re-fetches the list from the store. */
  refresh: () => Promise<void>;
  /** Loads a full room document by id (hydrates the S3 body). */
  getById: (id: string) => Promise<Room | undefined>;
  /** Creates a new room (creator/members seeded from identity). */
  create: (draft: RoomDraft) => Promise<Room>;
  /** Updates an existing room document in place. */
  update: (room: Room) => Promise<Room>;
  /**
   * Adds members to a room (skipping aliases already present, preserving
   * existing roles) and re-saves it so the pointer's mirrored aliases update.
   */
  addMembers: (id: string, members: RoomMember[]) => Promise<Room | undefined>;
  /** Soft-deletes a room. */
  remove: (id: string) => Promise<void>;
}

export function useRooms(): UseRoomsResult {
  const api = useApi();
  const store = useMemo(() => new RoomStore(api), [api]);

  const [summaries, setSummaries] = useState<RoomSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await store.listSummaries();
      setSummaries(sortRoomsByRecency(rows));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load rooms.');
    } finally {
      setLoading(false);
    }
  }, [store]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const getById = useCallback((id: string) => store.get(id), [store]);

  const create = useCallback(
    async (draft: RoomDraft) => {
      const saved = await store.create(draft);
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const update = useCallback(
    async (room: Room) => {
      const saved = await store.update(room);
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const addMembers = useCallback(
    async (id: string, members: RoomMember[]) => {
      const saved = await store.addMembers(id, members);
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await store.softDelete(id);
      await refresh();
    },
    [store, refresh],
  );

  return {
    summaries,
    loading,
    error,
    refresh,
    getById,
    create,
    update,
    addMembers,
    remove,
  };
}
